import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { serverTimestamp } from '@angular/fire/firestore';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Assignment } from '../../../../models/assignment.school.model';
import { Group } from '../../../../models/group.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolAssignmentService } from '../../../../services/assignment.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as assignmentActions from '../../../../state/actions/assignment.school.actions';
import { AppState } from '../../../../state/appstate';
import * as assignmentSelector from '../../../../state/selectors/assignment.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { RichTextEditorComponent } from '../../../components/quill-editor/rich-text-editor/rich-text-editor.component';

@Component({
  selector: 'app-edit-assignment-notes',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnActionComponent,
    AsyncPipe,
    RichTextEditorComponent,
  ],
  templateUrl: './edit-assignment-notes.component.html',
  styleUrl: './edit-assignment-notes.component.css',
})
export class EditAssignmentNotesComponent {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  assignmentService: SchoolAssignmentService = inject(SchoolAssignmentService);

  htmlData: string = '';
  // @ViewChild('textEditor', { static: false }) textEditor!: TextEditorComponent;

  groupId: string | null | undefined = undefined;
  assignmentId: string | null | undefined = undefined;

  group: Group | undefined | null = undefined;
  user$: Observable<UserModel | null> = new Observable();
  assignment: Assignment | null | undefined;

  loading$: Observable<boolean> = new Observable();

  working: boolean = false;

  exit$: Subject<void> = new Subject<void>();

  params$ = this.activatedRoute.paramMap.pipe(
    map((params) => ({
      groupId: params.get('id'),
      assignmentId: params.get('assignmentId'),
    })),
    takeUntil(this.exit$),
  );

  myRoles$ = this.params$.pipe(
    switchMap((p) =>
      this.store.select(groupSelector.getMyRolesInGroup(p.groupId!)),
    ),
  );

  constructor() {
    this.activatedRoute.paramMap.subscribe((params) => {
      this.groupId =
        params.get('id') ||
        this.activatedRoute.parent?.snapshot.paramMap.get('id');
      this.assignmentId =
        params.get('assignmentId') ||
        this.activatedRoute.parent?.snapshot.paramMap.get('assignmentId');

      if (this.groupId && this.assignmentId) {
        this.myRoles$ = this.store.select(
          groupSelector.getMyRolesInGroup(this.groupId),
        );
      }
    });

    this.user$ = this.store.select((state) => state.user.user);
  }

  ngOnInit(): void {
    this.store
      .select(groupSelector.getGroupById(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.fetchAssignmentData(this.groupId!, this.assignmentId!);
      });
  }

  private fetchAssignmentData(groupId: string, assignmentId: string) {
    this.store
      .select(
        assignmentSelector.getAssignmentById({
          groupId: this.groupId!,
          assignmentId: this.assignmentId!,
          academicYear: this.group?.academicYear!,
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe(async (assignment) => {
        console.debug(
          `EditAssignmentNotesComponent :: ngOnInit: AssignmentId: ${assignment?.id}, GroupId: ${this.groupId}`,
        );
        if (assignment == undefined || assignment == null) {
          this.store.dispatch(
            assignmentActions.getAssignment({
              groupId: this.groupId!,
              assignmentId: this.assignmentId!,
            }),
          );
          return;
        }
        this.assignment = assignment;
        if (this.assignment?.assignmentUrl == null) {
          return;
        }
        try {
          this.loading$ = of(true);
          const response = await this.assignmentService.getAssignmentDocument(
            `${this.assignment?.assignmentUrl!}?_=${Date.now()}`,
          );
          if (response) {
            this.htmlData = JSON.stringify(response);
          }
          this.loading$ = of(false);
        } catch (error) {
          console.error(
            `EditAssignmentNotesComponent :: ngOnInit: Error fetching assignment notes: ${error}`,
          );
          this.toastr.error(
            'Error occurred while fetching assignment notes. Please try again later.',
            'Error Fetching Assignment',
          );
          this.loading$ = of(false);
          return;
        }
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onContentChanged(event: any): void {
    this.htmlData = event;
  }

  async onPublish(): Promise<void> {
    this.loading$ = of(true);

    if (
      !this.groupId ||
      !this.assignmentId ||
      !this.assignment?.id ||
      !this.assignment?.gradeId
    ) {
      this.logger.error(
        `EditAssignmentNotesComponent :: onPublish: Missing required information. groupId: ${this.groupId}, assignmentId: ${this.assignmentId}, assignment?.id: ${this.assignment?.id}, assignment?.gradeId: ${this.assignment?.gradeId}`,
      );
      this.toastr.error(
        `Pre-requisite information to upload notes is missing. Please try again later.`,
        'Error Publishing Assignment',
      );
      this.loading$ = of(false);
      return;
    }

    try {
      const metadata = {
        groupId: this.groupId,
        gradeId: this.assignment?.gradeId,
        academicYear: this.assignment?.academicYear,
        assignmentId: this.assignment?.id,
      };

      // call two promises and wait for them to complete
      const [response, assignmentData] = await Promise.all([
        await this.assignmentService.getUploadAssignmentUrl({
          groupId: this.groupId!,
          gradeId: this.assignment?.gradeId!,
          academicYear: this.assignment?.academicYear!,
          assignmentId: this.assignment?.id!,
          fileExtension: 'json',
          metadata: metadata,
        }),
        this.htmlData,
      ]);

      const finalUrl = UtilsService.replaceDomain(
        response.url.split('?')[0],
        environment['AWS_GROUPS_CLOUDFRONT'],
      )!;

      // Call AWS SignedUrl to upload the assignment
      this.assignmentService
        .uploadAssignmentDocument(response.url, assignmentData)
        .then((result) => {
          // console.log(`Uploaded assignment Notes Url: ${JSON.stringify(response)}`);
          this.store.dispatch(
            assignmentActions.update({
              groupId: this.groupId!,
              assignmentId: this.assignmentId!,
              assignment: {
                assignmentUrl: finalUrl,
                modified: serverTimestamp(),
              } as Assignment,
            }),
          );
        })
        .catch((error: any) => {
          this.toastr.error(
            `An error occurred while uploading assignment notes. ${error?.message}. Please try again later.`,
            'Error Publishing Assignment',
          );
          this.store.dispatch(
            assignmentActions.updateFailure({
              error: {
                code: 'Assignment Upload Error',
                title: 'Error Publishing Assignment',
                message: `An error occurred, '${error.message}'. Please try again later.`,
              } as AppExceptionModel,
            }),
          );
        });
      this.loading$ = of(false);
    } catch (error: any) {
      this.toastr.error(
        `An error occurred during assignment upload. ${error?.message}. Please try again later.`,
        'Error Publishing Assignment',
      );
      this.store.dispatch(
        assignmentActions.updateFailure({
          error: {
            code: 'Assignment Upload Error',
            title: 'Error Publishing Assignment',
            message: `An error occurred while uploading assignment. Please try again later.`,
          } as AppExceptionModel,
        }),
      );
      this.loading$ = of(false);
    }
  }
}
